import { useCallback, useSyncExternalStore } from "react";
import { roomElementsSubscription } from "@/lib/firestore/subscriptions/room-elements";
import { NO_WEDDING_KEY, planScopedKey } from "@/lib/firestore/subscription-keys";
import {
  EMPTY_ROOM_ELEMENTS,
  ROOM_ELEMENTS_SNAPSHOT_EMPTY,
  ROOM_ELEMENTS_SNAPSHOT_LOADING,
} from "@/lib/firestore/subscription-snapshots";
import { useWedding } from "./useWedding";
import type { RoomElement } from "@amazing-wedding/shared-types";

interface UseRoomElementsResult {
  elements: RoomElement[];
  loading: boolean;
}

/**
 * Subscribes to /weddings/{wid}/seatingPlans/{planId}/roomElements.
 *
 * Backed by a refcounted shared Firestore subscription keyed by weddingId::planId.
 */
export function useRoomElements(planId: string | null | undefined): UseRoomElementsResult {
  const { wedding, loading: weddingLoading } = useWedding();
  const weddingId = wedding?.id;
  const ready = Boolean(weddingId && planId);
  const key = ready ? planScopedKey(weddingId!, planId!) : NO_WEDDING_KEY;

  const subscribe = useCallback(
    (listener: () => void) => {
      if (weddingLoading || !ready) return () => {};
      return roomElementsSubscription.subscribe(key, listener);
    },
    [weddingLoading, ready, key],
  );

  const getSnapshot = useCallback(() => {
    if (weddingLoading) return ROOM_ELEMENTS_SNAPSHOT_LOADING;
    if (!ready) return ROOM_ELEMENTS_SNAPSHOT_EMPTY;
    return roomElementsSubscription.getSnapshot(key);
  }, [weddingLoading, ready, key]);

  const snapshot = useSyncExternalStore(
    subscribe,
    getSnapshot,
    () => ROOM_ELEMENTS_SNAPSHOT_LOADING,
  );

  if (weddingLoading) {
    return { elements: EMPTY_ROOM_ELEMENTS, loading: true };
  }
  if (!ready) {
    return { elements: EMPTY_ROOM_ELEMENTS, loading: false };
  }

  return { elements: snapshot.data, loading: snapshot.loading };
}
